import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { tokenExpiredEvent } from '../tokenExpiredEvent';
import { ErrorCodes, ErrorMessages, isErrorResponse } from './constants';
import { ApiResponse, ApiError, RequestConfig, IResponse } from './types';

const baseUrl = process.env.REACT_APP_BASE_URL;

const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: baseUrl,
    timeout: 50000,
  });
  instance.interceptors.request.use((config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  return instance;
};

const getAuthToken = (): string | null => {
  const auth = JSON.parse(localStorage.getItem('auth') || '{}');
  return auth.token;
};

class HttpClient {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = createAxiosInstance();
  }

  private handleResponse<T>(response: AxiosResponse<IResponse<T>>): T {
    const apiReturn = response.data;

    // Only handle actual token expiration (code 101)
    // Note: 2002 (UNAUTHORIZED) can be other authorization issues (e.g., insufficient permissions)
    // and should not trigger the token expired event
    const codeStr = apiReturn.code?.toString();
    if (codeStr === '101') {
      tokenExpiredEvent.emit();
      const error: ApiError = {
        errorCode: ErrorCodes.TOKEN_EXPIRED,
        errorMsg: ErrorMessages[ErrorCodes.TOKEN_EXPIRED],
      };
      throw error;
    }

    // Handle business errors (code !== "0000" means error)
    if (isErrorResponse(apiReturn)) {
      // Use server's message if available, otherwise fall back to predefined message or data field
      let errorMsg = apiReturn.message;

      // If message is generic, try to get more specific error from data field
      if (!errorMsg || errorMsg === 'Operating Failed') {
        errorMsg = typeof apiReturn.data === 'string' ? apiReturn.data : (apiReturn.message || ErrorMessages[codeStr] || 'Unknown error');
      }

      const error: ApiError = {
        errorCode: Number(apiReturn.code) || -1,
        errorMsg: errorMsg,
      };

      throw error;
    }

    return apiReturn.data;
  }

  private handleError(error: AxiosError): never {
    if (error.code === 'ERR_NETWORK') {
      const apiError: ApiError = {
        errorCode: ErrorCodes.NETWORK_ERROR,
        errorMsg: 'Network Error',
      };
      throw apiError;
    }

    const apiError: ApiError = {
      errorCode: ErrorCodes.OTHER,
      errorMsg: error.message,
    };
    throw apiError;
  }

  async get<T = any>(
    url: string,
    params: Record<string, any> = {},
    config?: RequestConfig
  ): Promise<T> {
    try {
      const response = await this.axiosInstance.get<IResponse<T>>(url, {
        params,
        timeout: config?.timeout,
        headers: config?.headers,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return this.handleError(error);
      }
      throw error;
    }
  }

  async post<T = any>(
    url: string,
    data?: Record<string, any>,
    params?: Record<string, any>,
    config?: RequestConfig
  ): Promise<T> {
    try {
      const response = await this.axiosInstance.post<IResponse<T>>(url, data, {
        timeout: config?.timeout,
        params: params,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return this.handleError(error);
      }
      throw error;
    }
  }

  async put<T = any>(
    url: string,
    data?: Record<string, any>,
    params?: Record<string, any>,
    config?: RequestConfig
  ): Promise<T> {
    try {
      const response = await this.axiosInstance.put<IResponse<T>>(url, data, {
        timeout: config?.timeout,
        params: params,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return this.handleError(error);
      }
      throw error;
    }
  }

  async patch<T = any>(
    url: string,
    data?: Record<string, any>,
    params?: Record<string, any>,
    config?: RequestConfig
  ): Promise<T> {
    try {
      const response = await this.axiosInstance.patch<IResponse<T>>(url, data, {
        timeout: config?.timeout,
        params: params,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return this.handleError(error);
      }
      throw error;
    }
  }

  async delete<T = any>(
    url: string,
    params: Record<string, any> = {},
    config?: RequestConfig
  ): Promise<T> {
    try {
      const response = await this.axiosInstance.delete<IResponse<T>>(url, {
        params,
        timeout: config?.timeout,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return this.handleError(error);
      }
      throw error;
    }
  }

  async legacyGet<T = any>(
    endpoint: string,
    params: Record<string, any> = {},
    config?: RequestConfig
  ): Promise<T> {
    return this.post<T>(endpoint, params, config);
  }

  getToken(): string | null {
    return getAuthToken();
  }
}

export const httpClient = new HttpClient();
